/**
 * Generate a PNG thumbnail from a GLB (3D model) file using model-viewer.
 *
 * Creates a model-viewer element, loads the GLB, waits for render,
 * and captures a screenshot via model-viewer's toBlob() API.
 *
 * Key: model-viewer uses IntersectionObserver and won't load offscreen elements.
 * We use loading="eager" and keep the element in the viewport but visually hidden
 * (opacity:0, pointer-events:none) so the model loads and renders.
 *
 * IMPORTANT: model-viewer shares a single WebGL renderer across all instances.
 * Concurrent rendering causes toBlob() to capture the wrong model. We serialize
 * all thumbnail generation through a queue so only one model-viewer exists at a time.
 */
import '@google/model-viewer';

const THUMBNAIL_SIZE = 512;
const RENDER_TIMEOUT_MS = 30000;
const RENDER_SETTLE_MS = 1000;

// Serialization queue — ensures only one model-viewer renders at a time
let renderQueue: Promise<void> = Promise.resolve();

function renderSingle(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    // Must remain in viewport for model-viewer to render, but visually hidden
    container.style.cssText = `position:fixed;top:0;left:0;width:${THUMBNAIL_SIZE}px;height:${THUMBNAIL_SIZE}px;opacity:0;pointer-events:none;z-index:-1;`;
    document.body.appendChild(container);

    const viewer = document.createElement('model-viewer') as any;
    const blobUrl = URL.createObjectURL(file);
    viewer.src = blobUrl;
    viewer.setAttribute('loading', 'eager');
    viewer.setAttribute('reveal', 'auto');
    viewer.setAttribute('auto-rotate', '');
    viewer.setAttribute('shadow-intensity', '1');
    viewer.setAttribute('environment-image', 'neutral');
    viewer.style.cssText = `width:${THUMBNAIL_SIZE}px;height:${THUMBNAIL_SIZE}px;`;
    container.appendChild(viewer);

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(blobUrl);
      container.remove();
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('GLB thumbnail generation timed out'));
    }, RENDER_TIMEOUT_MS);

    viewer.addEventListener('load', async () => {
      try {
        // Let the renderer settle for a cleaner capture
        await new Promise(r => setTimeout(r, RENDER_SETTLE_MS));
        const blob: Blob = await viewer.toBlob({ mimeType: 'image/png', idealAspect: true });
        clearTimeout(timeout);
        cleanup();
        resolve(blob);
      } catch (err) {
        clearTimeout(timeout);
        cleanup();
        reject(err);
      }
    });

    viewer.addEventListener('error', () => {
      clearTimeout(timeout);
      cleanup();
      reject(new Error('Failed to load 3D model for thumbnail'));
    });
  });
}

export async function generateGlbThumbnail(file: File): Promise<Blob> {
  // Chain onto the queue so only one render happens at a time
  const result = renderQueue.then(
    () => renderSingle(file),
    () => renderSingle(file), // proceed even if previous item failed
  );
  // Advance the queue (swallow errors so one failure doesn't block the rest)
  renderQueue = result.then(() => {}, () => {});
  return result;
}
